import { onMounted, onUnmounted } from 'vue';
import { showToastError } from '@/helpers';
import { NestApiType, nestCall } from '../nestApi';

// composables/useFacebookLogin.ts
// eslint-disable-next-line import/prefer-default-export
type WhatsappConnectHooks = {
  onBackendWorkStart?: () => void;
};

export function useFacebookLogin(shopId: number | string, appId: string, version: string) {
  let messageHandler: (event: MessageEvent) => void;
  let signupInfo: { business_id: string; phone_number_id: string; waba_id: string } | null = null;
  let signupAbortedToastShown = false;

  const notifySignupAborted = async () => {
    if (signupAbortedToastShown) {
      return;
    }
    signupAbortedToastShown = true;
    await showToastError('NO_SIGNUP_INFO');
  };

  const getErrorCode = (response: any): string => (
    response?.error?.code
      || response?.error?.error
      || response?.error?.message
      || response?.error
      || response?.code
      || response?.message
      || ''
  );

  const handleWhatsappConnectError = async (response: any): Promise<never> => {
    const errorCode = getErrorCode(response);
    const translationMap: Record<string, string> = {
      whatsapp_business_already_connected: 'WHATSAPP_BUSINESS_ALREADY_CONNECTED',
      phone_number_not_found_in_waba: 'WHATSAPP_PHONE_NOT_FOUND_IN_WABA',
      vonage_number_not_verified: 'WHATSAPP_NUMBER_NOT_VERIFIED',
      vonage_number_registration_failed: 'WHATSAPP_REGISTER_FAILED',
      vonage_app_link_failed: 'WHATSAPP_LINK_FAILED',
      meta_subscribe_failed: 'WHATSAPP_META_SUBSCRIBE_FAILED',
      whatsapp_connect_failed: 'FAILED_TO_FETCH_WABA_DATA',
    };

    await showToastError(translationMap[errorCode] || 'FAILED_TO_FETCH_WABA_DATA');
    throw new Error(errorCode || 'whatsapp_connect_failed');
  };

  const removeMessageListener = () => {
    window.removeEventListener('message', messageHandler);
  };

  const loadFacebookSDK = (): Promise<void> => new Promise((resolve) => {
    if (window.FB) return resolve();

    window.fbAsyncInit = function () {
      window.FB.init({
        appId,
        cookie: true,
        xfbml: false, // explained below
        version,
      });
      resolve();
    };

    if (!document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      document.head.appendChild(script);
    }
  });

  const initMessageListener = () => {
    messageHandler = (event: MessageEvent) => {
      if (
        event.origin !== 'https://www.facebook.com'
        && event.origin !== 'https://web.facebook.com'
      ) {
        return;
      }

      try {
        const data = JSON.parse(event.data);
        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          if (data.event === 'FINISH') {
            const { business_id, phone_number_id, waba_id } = data.data;
            signupInfo = {
              business_id,
              phone_number_id,
              waba_id,
            };
          } else if (data.event === 'CANCEL') {
            signupInfo = null;
            void notifySignupAborted();
          } else if (data.event === 'ERROR') {
            signupInfo = null;
            void notifySignupAborted();
          }
        }
      } catch {
      }
    };

    window.addEventListener('message', messageHandler);
  };

  const fbLoginCallback = async (
    response: any,
    callback?: () => void | Promise<void>,
    hooks?: WhatsappConnectHooks,
  ) => {
    hooks?.onBackendWorkStart?.();

    if (!signupInfo) {
      await notifySignupAborted();
      throw new Error('No signup info available');
    }

    const precheckRes = await nestCall('post', NestApiType.MESSAGING, '/whatsapp/precheck-account-usage', {}, '', {
      shop_id: Number(shopId),
      ...signupInfo,
    });

    if (precheckRes?.error) {
      return handleWhatsappConnectError(precheckRes);
    }

    const res = await nestCall('post', NestApiType.MESSAGING, '/whatsapp/connect-whatsapp-business', {}, '', {
      code: response.authResponse.code,
      shop_id: Number(shopId),
      ...signupInfo,
    });

    if (res?.error) {
      return handleWhatsappConnectError(res);
    }

    if (!res?.success) {
      return handleWhatsappConnectError({
        error: { code: res?.error || 'whatsapp_connect_failed' },
      });
    }

    if (callback) await callback();
    return res;
  };

  const loginAndConnectWABA = (
    callback?: () => void | Promise<void>,
    hooks?: WhatsappConnectHooks,
  ): Promise<any> => new Promise((resolve, reject) => {
    signupInfo = null;
    signupAbortedToastShown = false;

    window.FB.login((response: any) => {
      (async () => {
        try {
          if (!response?.authResponse?.code) {
            if (!signupInfo) {
              await notifySignupAborted();
              reject(new Error('No signup info available'));
            }
            resolve(null);
            return;
          }
          const res = await fbLoginCallback(response, callback, hooks);
          resolve(res);
        } catch (err) {
          reject(err);
        }
      })();
    },
    {
      config_id: process.env.VUE_APP_FACEBOOK_CONFIGURATION_ID || '1206763611103618', // configuration ID goes here
      response_type: 'code', // must be set to 'code' for System User access token
      override_default_response_type: true, // when true, any response types passed in the "response_type" will take precedence over the default types
      extras: {
        feature: 'whatsapp_embedded_signup',
        version: 2,
        sessionInfoVersion: 3,
        setup: {
          solutionID: process.env.VUE_APP_VONAGE_SOLUTION_ID || '',
        },
      },
    });
  });

  onMounted(async () => {
    await loadFacebookSDK();
    initMessageListener();
  });

  onUnmounted(() => {
    removeMessageListener();
  });

  return {
    loginAndConnectWABA,
  };
}
